import { useState } from 'react';
import { Clear as ClearIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  IconButton,
  InputBase,
  Paper,
  type SxProps,
  type Theme,
  alpha,
} from '@mui/material';

interface SearchBarProps {
  placeholder?: string;
  onSearch?: (query: string) => void;
  onClear?: () => void;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
}

const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = 'Search...',
  onSearch,
  onClear,
  fullWidth = false,
  size = 'medium',
  sx,
}) => {
  const [query, setQuery] = useState('');

  const handleSearch = (value: string) => {
    setQuery(value);
    onSearch?.(value);
  };

  const handleClear = () => {
    setQuery('');
    onClear?.();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: fullWidth ? '100%' : 'auto',
        mb: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        '&:hover': {
          borderColor: 'primary.main',
        },
        '&:focus-within': {
          borderColor: 'primary.main',
          boxShadow: (theme) =>
            `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
        },
        ...sx,
      }}
    >
      <IconButton
        sx={{
          p: size === 'small' ? 1 : 1.5,
          color: 'text.secondary',
        }}
        aria-label="search"
      >
        <SearchIcon />
      </IconButton>
      <InputBase
        sx={{
          ml: 1,
          flex: 1,
          fontSize: (theme) =>
            size === 'small'
              ? theme.typography.body2.fontSize
              : theme.typography.body1.fontSize,
        }}
        placeholder={placeholder}
        value={query}
        onChange={(e) => handleSearch(e.target.value)}
      />
      {query && (
        <IconButton
          sx={{
            p: size === 'small' ? 1 : 1.5,
            color: 'text.secondary',
          }}
          onClick={handleClear}
          aria-label="clear"
        >
          <ClearIcon />
        </IconButton>
      )}
    </Paper>
  );
};

export default SearchBar;
